import { useParams, useNavigate } from 'react-router-dom';
import { useOdoo } from '../../context/OdooContext.js';
import { useCctpProject } from '../../hooks/useCctpProject.js';
import { useMdd, LOT_ICONS } from '../../hooks/useMdd.js';

export default function ProjectOverviewScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { client } = useOdoo();
  const { project, lots, loading } = useCctpProject(client, id ? parseInt(id) : null);
  const mdd = useMdd();

  if (loading) return <div className="min-h-screen bg-bg flex items-center justify-center text-ink-3">Chargement…</div>;
  if (!project) return null;

  return (
    <div className="min-h-screen bg-bg">
      <header className="bg-white border-b border-line px-8 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/')} className="text-ink-3 hover:text-ink transition-colors text-sm">← Projets</button>
        <div className="h-4 w-px bg-line" />
        <h1 className="font-bold text-ink">{project.name}</h1>
        <span className="ml-auto text-xs font-mono bg-brand-soft text-brand-dark px-2 py-0.5 rounded-md">
          Indice {project.indice ?? 'A'}
        </span>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header project card */}
        <div className="bg-white rounded-2xl shadow-card p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Maître d'ouvrage", value: project.maitre_ouvrage },
              { label: 'Adresse', value: project.adresse },
              { label: 'Puissance PV', value: project.puissance_pv_kwc ? `${project.puissance_pv_kwc} kWc` : '—' },
              { label: 'Capacité BESS', value: project.capacite_bess_kwh ? `${project.capacite_bess_kwh} kWh` : '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs text-ink-3 mb-0.5">{label}</p>
                <p className="text-sm font-semibold text-ink">{(value as string) || '—'}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-line flex items-center justify-between">
            <div>
              <p className="text-xs text-ink-3">Estimation totale</p>
              <p className="text-2xl font-bold text-ink">
                {project.estimation_total_ht ? `${Number(project.estimation_total_ht).toLocaleString('fr-FR')} €` : '—'}
                <span className="text-sm text-ink-3 font-normal ml-1">HT</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-ink-3">Crédits IA</p>
              <p className="text-lg font-bold text-brand">{project.credits_ia ?? 0}</p>
            </div>
          </div>
        </div>

        {/* 12 lots grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-ink">12 Lots du projet</h2>
            <button
              onClick={() => navigate(`/projects/${id}/lots`)}
              className="text-sm text-brand hover:text-brand-dark font-semibold transition-colors"
            >
              Vue tableau →
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {mdd.lots.map(lot => {
              const lotData = lots[lot.code];
              const hasData = !!lotData;
              return (
                <button
                  key={lot.code}
                  onClick={() => navigate(`/projects/${id}/lots/${lot.code}`)}
                  className="bg-white rounded-xl shadow-card p-4 text-left hover:shadow-lg transition-shadow border border-transparent hover:border-brand/20"
                >
                  <div className="text-2xl mb-2">{LOT_ICONS[lot.code]}</div>
                  <div className="text-xs font-semibold text-ink-3 mb-0.5">Lot {lot.num}</div>
                  <div className="text-sm font-bold text-ink leading-tight">{lot.label}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${hasData ? 'bg-green' : 'bg-line'}`} />
                    <span className="text-xs text-ink-3">{hasData ? 'En cours' : 'Vide'}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
